import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Eye,
  EyeOff,
  AlertCircle,
  ArrowRight,
  Shield,
  CheckCircle,
  Users,
  Zap
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import TurnstileWidget from "../../components/TurnstileWidget";
import { toast } from "react-hot-toast";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);  
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileError, setTurnstileError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [shouldResetTurnstile, setShouldResetTurnstile] = useState(false);
  const turnstileRef = useRef(null);

  const from = location.state?.from?.pathname || "/dashboard";
  const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || "0x4AAAAAACpuPdR7k67UMxra";

  // Mouse tracking for interactive background
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Password strength checker
  useEffect(() => {
    let strength = 0;
    if (password.length > 0) {
      if (password.length >= 8) strength++;
      if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
      if (/\d/.test(password)) strength++;
      if (/[^a-zA-Z0-9]/.test(password)) strength++;
    }
    setPasswordStrength(strength);
  }, [password]);

  // Function to reset Turnstile
  const resetTurnstile = () => {
    setTurnstileToken(null);
    setTurnstileError("");
    setShouldResetTurnstile(true);
    // Reset the flag after a short delay
    setTimeout(() => setShouldResetTurnstile(false), 100);
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    // Validation
    if (!username.trim()) {
      toast.error("Please enter your username");
      setLoading(false);
      return;
    }

    if (!password) {
      toast.error("Please enter your password");
      setLoading(false);
      return;
    }

    if (!turnstileToken) {
      toast.error("Please complete the human verification");
      setLoading(false);
      return;
    }

    try {
      await login(username.trim(), password, turnstileToken);
      toast.success("Login successful! Welcome back.");
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      const msg = err?.response?.data?.message ||
        err?.response?.data?.errors?.[0]?.message ||
        err?.message ||
        "Invalid username or password.";
      
      // Show error as toast
      toast.error(msg);
      
      // 🔄 Reset Turnstile on error
      resetTurnstile();
      
      // Show a toast asking user to verify again
      toast.info("Please complete the human verification again.", {
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }

  const getStrengthColor = () => {
    if (password.length === 0) return "bg-gray-200";
    if (passwordStrength <= 1) return "bg-red-500";
    if (passwordStrength === 2) return "bg-orange-500";
    if (passwordStrength === 3) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getStrengthText = () => {
    if (password.length === 0) return "";
    if (passwordStrength <= 1) return "Weak";
    if (passwordStrength === 2) return "Fair";
    if (passwordStrength === 3) return "Good";
    return "Strong";
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 overflow-hidden">
      {/* Background blobs */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute -left-32 -top-32 w-96 h-96 bg-emerald-400/20 rounded-full blur-3xl animate-pulse"
          style={{
            transform: `translate(${(mousePosition.x - 50) * 0.02}px, ${(mousePosition.y - 50) * 0.02}px)`
          }}
        />
        <div
          className="absolute -bottom-40 -right-24 w-[28rem] h-[28rem] bg-green-400/20 rounded-full blur-3xl animate-pulse delay-1000"
          style={{
            transform: `translate(${(mousePosition.x - 50) * -0.02}px, ${(mousePosition.y - 50) * -0.02}px)`
          }}
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] bg-emerald-300/10 rounded-full blur-3xl" />
      </div>

      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12">
        <div className="relative z-10 text-center max-w-lg">
          <div className="relative mb-10">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-emerald-400 to-green-500 blur-2xl opacity-50 animate-pulse" />
            <div className="relative inline-flex items-center justify-center w-32 h-32 rounded-3xl bg-gradient-to-br from-emerald-500 to-green-600 shadow-2xl shadow-emerald-500/25 animate-float">
              <img src="/logo.jpg" alt="Alfennzo Logo" className="w-full h-full rounded-lg object-contain" />
            </div>
          </div>

          <h1 className="text-5xl font-bold text-gray-800 mb-4">
            Alfennzo Management
          </h1>
          <p className="text-gray-600 text-lg mb-8">
            Your trusted partner for seamless collaboration and real-time insights
          </p>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-emerald-200 shadow-sm">
              <Shield className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-xs text-gray-700 font-medium">256-bit SSL</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-emerald-200 shadow-sm">
              <Users className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-xs text-gray-700 font-medium">Team Access</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-emerald-200 shadow-sm">
              <Zap className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-xs text-gray-700 font-medium">Real-time</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-6 lg:px-12 py-12 relative z-10">
        <div className="w-full max-w-md">
          {/* Mobile header */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 shadow-lg shadow-emerald-500/25 mb-4">
              <img src="/logo.jpg" alt="Alfennzo Logo" className="w-full h-full object-contain rounded-lg" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Alfennzo</h1>
            <p className="text-gray-600 text-sm mt-1">Management</p>
          </div>

          {/* Form Card */}
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl shadow-emerald-200/50 p-8 border border-emerald-100">
            <div className="hidden lg:block mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-emerald-400 to-green-500 rounded-full" />
                <h2 className="text-2xl font-bold text-gray-800">Welcome Back</h2>
              </div>
              <p className="text-gray-600 text-sm ml-4">
                Sign in to access your partner dashboard
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Username Field */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Username <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    autoFocus
                    type="text"
                    placeholder="Enter your partner ID"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    required
                    className="w-full pl-4 pr-10 py-2.5 bg-white border border-gray-300 text-gray-800 placeholder-gray-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl transition-all duration-200 outline-none"
                  />
                  {username.length > 0 && (
                    <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
                  )}
                </div>
              </div>

              {/* Password Field */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    className="w-full pl-4 pr-12 py-2.5 bg-white border border-gray-300 text-gray-800 placeholder-gray-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl transition-all duration-200 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Cloudflare Turnstile */}
              <div className="mb-4 flex justify-center">
                <TurnstileWidget
                  ref={turnstileRef}
                  sitekey={SITE_KEY}
                  onVerify={(token) => {
                    setTurnstileToken(token);
                    setTurnstileError("");
                    console.log("Turnstile verified successfully");
                  }}
                  onError={() => {
                    setTurnstileError("Verification failed");
                    setTurnstileToken(null);
                    toast.error("Human verification failed. Please try again.");
                  }}
                  onExpire={() => {
                    setTurnstileToken(null);
                    setTurnstileError("Verification expired");
                    toast.warning("Verification expired. Please complete again.");
                  }}
                  reset={shouldResetTurnstile}
                />
              </div>

              {/* Turnstile Error */}
              {turnstileError && (
                <div className="mb-4 flex items-center gap-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{turnstileError}</span>
                </div>
              )}

              {/* Forgot Links */}
              <div className="flex items-center justify-between text-sm mb-6">
                <Link
                  to="/forgot-username"
                  className="text-emerald-600 hover:text-emerald-700 font-semibold transition-colors"
                >
                  Forgot username?
                </Link>
                <Link
                  to="/forgot-password"
                  className="text-emerald-600 hover:text-emerald-700 font-semibold transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="relative w-full py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 hover:scale-[1.02] transition-all duration-300 overflow-hidden group disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </>
                  )}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-green-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}